import React, {useCallback} from 'react';
import Header from "@amzn/awsui-components-react-v3/polaris/header";
import {
    Alert,
    Box,
    Button,
    Container,
    Form as AWSUIForm,
    Link,
    Spinner
} from "@amzn/awsui-components-react-v3";
import {DomainTracker} from "src/model/DomainTracker";
import {getUserAlias} from "src/utils/cookie-helper";
import {AppRoutes} from "src/components/common/router-common";
import {toast} from "react-toastify";
import {WorkflowInstanceUtil} from "src/utils/workflow-instance-util";
import {TabInputForm, WORKFLOW_EXECUTION_SHARED_DATA} from "src/components/common/tab-input-form";
import {useStartScreenUtil} from "src/components/start-workflow-instance/start-screen-util-hook-v1";
import {
    ScheduleWorkflowMetadata,
    WorkflowMetadataV1
} from "src/components/start-workflow-instance/start-screen-components";

/**
 * Start new workflow in Adhoc or Scheduled(later) manner with JSON schema in tabular view
 * @constructor
 */
export function StartWorkflowInstanceContentV1() {
    const {
        capabilitySelectedOption,
        capabilityOptions,
        primitiveSelectedOption,
        primitiveOptions,
        onCapabilityChangeHandler,
        onPrimitiveChangeHandler,
        workflowDefinitionOption,
        workflowDefinitionOptions,
        onWorkflowDefinitionChangeHandler,
        schemaLoading,
        startWFLoading,
        workflowDetails,
        enableLiveValidate,
        workflowInput,
        adhocStartRecipeExecutionsResult,
        classificationTagSelectedOption,
        subClassificationTagOptions,
        selectedSubClassificationTag,
        domainName,
        classificationTagOptions,
        targetWebsite,
        targetWebsiteOptions,
        workflowUISchema,
        onTabFormDataChange,
        onTabFormDataError,
        errorText,
        itemsRef,
        onClassificationTagChangeHandler,
        onSubClassificationTagChangeHandler,
        onDomainChangeHandler,
        onTargetWebsiteChange,
        isScheduled,
        isScheduleDisabled,
        scheduleName,
        cronExpression,
        onScheduleNameChange,
        onCronExpressionChange,
        onScheduleStatusChange,
        resetFormValues,
        updateWorkflowLoadingStatus,
        updateAdhocStartRecipeExecutionsResult,
        onTabDataChangeHandler,
        onFormSubmitHandler
    } = useStartScreenUtil();

    // When user clicks start button, it triggers all JSON Schema start button
    function handleFormSubmit() {
        onFormSubmitHandler(() => {
            areContextInputsValid() && submitVaidatedForm()
        });
    }

    function areContextInputsValid(): boolean {
        if (isScheduled) {
            if ((scheduleName.trim() == "") || (cronExpression.trim() == "")) {
                toast.error("Schedule name and Cron expression can not be blank");
                return false;
            }
        }
        return true;
    }
    function createTags(classificationTag: string, subClassificationTag?: string): Record<string, any> {
        const tags: Record<string, any> = {
            classificationTag: {
                tagType: "STRING",
                value: classificationTag
            }
        };
        if (subClassificationTag) {
            tags.subClassificationTag = {
                tagType: "STRING",
                value: subClassificationTag
            };
        }
        return tags;
    }
    // Once schema is validate across all Tabs, trigerring start API.
    function submitVaidatedForm() {
        updateWorkflowLoadingStatus(true);
        const domainTracker = new DomainTracker();

        const tags = createTags(
            classificationTagSelectedOption.label || "",
            isScheduled ? scheduleName.trim() : selectedSubClassificationTag?.value
        );

        const recipeExecutionInput = WorkflowInstanceUtil.extractRecipeExecutionInput(workflowInput, WORKFLOW_EXECUTION_SHARED_DATA);

        if (isScheduled) {
            domainTracker.scheduleWorkflowV1(
                classificationTagSelectedOption.label || "",
                workflowDefinitionOption.value || "",
                getUserAlias(),
                capabilitySelectedOption.value || "",
                primitiveSelectedOption.value || "",
                scheduleName.trim(),
                `cron(${cronExpression.trim()})`,
                WorkflowInstanceUtil.trimObject(recipeExecutionInput),
                tags,
                WorkflowInstanceUtil.isDomainFieldVisible(classificationTagSelectedOption) ? domainName : undefined,
                targetWebsite.value
            )
                .then((resp) => {
                    toast.success("Successfully Scheduled Workflow Instances");
                    updateAdhocStartRecipeExecutionsResult({
                        executionId: undefined,
                        errorMessage: undefined
                    });
                    resetFormValues();
                    window.scrollTo({top: 0, behavior: 'smooth'});
                })
                .catch(err => {
                    try {
                        toast.error(err.message);
                    } catch (e) {
                        toast.error(JSON.stringify(err));
                    }
                })
                .then(() => {
                    updateWorkflowLoadingStatus(false);
                });
        } else {
            domainTracker.adhocStartWorkflowV1(
                classificationTagSelectedOption.label || "",
                workflowDefinitionOption.value || "",
                getUserAlias(),
                WorkflowInstanceUtil.trimObject(recipeExecutionInput),
                primitiveSelectedOption.value || "",
                capabilitySelectedOption.value || "",
                tags,
                WorkflowInstanceUtil.isDomainFieldVisible(classificationTagSelectedOption) ? domainName : undefined,
                targetWebsite.value
            )
                .then((resp) => {
                    if (resp.errorMessage) {
                        toast.error(resp.errorMessage);
                        updateAdhocStartRecipeExecutionsResult(resp);
                    } else {
                        toast.success("Successfully Started Workflow Instance");
                        resetFormValues();
                        updateAdhocStartRecipeExecutionsResult({
                            executionId: resp.executionId,
                            errorMessage: undefined
                        });
                        window.scrollTo({top: 0, behavior: 'smooth'});
                    }
                })
                .catch(err => {
                    try {
                        console.log(err);
                        toast.error(err.message)
                    } catch (e) {
                        toast.error(JSON.stringify(err))
                    }
                })
                .then(() => {
                    updateWorkflowLoadingStatus(false)
                })
        }
    }

    /**
     * Callback function is fired on submit action of forms.
     * the start screen contains multiple forms, ie every stage input is mapped to an individual form.
     * When user clicks on submit button (start workflow instance button), all forms are submitted, post which form
     * data is sent to backend.
     */
    const onTabFormDataSubmit = useCallback((tabId: string, tabIndex: number) => {
        onTabDataChangeHandler(tabId, tabIndex, () => {
            areContextInputsValid() && submitVaidatedForm()
        })
    }, [onTabDataChangeHandler, areContextInputsValid, submitVaidatedForm])

    return <>
        <Box padding={{bottom: "l"}}>
            <Alert visible={!!adhocStartRecipeExecutionsResult.executionId}
                   data-testid={"start-workflow-success-alert"}
                   type={"success"}
                   dismissible={true}
                   onDismiss={() => updateAdhocStartRecipeExecutionsResult({
                       executionId: undefined,
                       errorMessage: undefined
                   })}>
                <div>Successfully started Workflow Instance. View Instance detail <Link
                    href={AppRoutes.executionDetail(adhocStartRecipeExecutionsResult.executionId!)}>
                    {adhocStartRecipeExecutionsResult.executionId}
                </Link></div>
            </Alert>
        </Box>
        <AWSUIForm
            errorText={errorText}
            actions={
                <Button variant="primary"
                        data-testid={"start-workflow-instance-submit-button"}
                        disabled={schemaLoading || classificationTagSelectedOption.value == ""}
                        loading={startWFLoading}
                        onClick={() => handleFormSubmit()}>Start Workflow Instance</Button>

            }
        >

            <Container header={<Header variant="h2">Start Workflow Instance</Header>}>
                <WorkflowMetadataV1
                    capabilitySelectedOption={capabilitySelectedOption}
                    capabilityOptions={capabilityOptions}
                    primitiveSelectedOption={primitiveSelectedOption}
                    primitiveOptions={primitiveOptions}
                    onCapabilityChangeHandler={onCapabilityChangeHandler}
                    onPrimitiveChangeHandler={onPrimitiveChangeHandler}
                    workflowDefinitionOption={workflowDefinitionOption}
                    workflowDefinitionOptions={workflowDefinitionOptions}
                    onWorkflowDefinitionChangeHandler={onWorkflowDefinitionChangeHandler}
                    classificationTagSelectedOption={classificationTagSelectedOption}
                    classificationTagOptions={classificationTagOptions}
                    selectedSubClassificationTag={selectedSubClassificationTag}
                    subClassificationTagOptions={subClassificationTagOptions}
                    targetWebsite={targetWebsite}
                    targetWebsiteOptions={targetWebsiteOptions}
                    domainName={domainName}
                    isScheduled={isScheduled}
                    startWFLoading={startWFLoading}
                    schemaLoading={schemaLoading}
                    isScheduleDisabled={isScheduleDisabled}
                    onClassificationTagChangeHandler={onClassificationTagChangeHandler}
                    onSubClassificationTagChangeHandler={onSubClassificationTagChangeHandler}
                    onDomainChange={onDomainChangeHandler}
                    onTargetWebsiteChange={onTargetWebsiteChange}
                    onScheduleStatusChange={onScheduleStatusChange}
                />
                <ScheduleWorkflowMetadata
                    isScheduled={isScheduled}
                    scheduleName={scheduleName}
                    cronExpression={cronExpression}
                    onScheduleNameChange={onScheduleNameChange}
                    onCronExpressionChange={onCronExpressionChange}
                />
                <Container
                    data-testid={"workflow-input-rjsf-form-field"}
                    header={<Header variant={"h3"}>Workflow Input</Header>}
                >
                    {!capabilitySelectedOption.value ? (
                        <Alert>Please select capability to provide workflow input</Alert>
                    ) : !primitiveSelectedOption.value ? (
                        <Alert>Please select primitive to provide workflow input</Alert>
                    ) : !workflowDefinitionOption.value ? (
                        <Alert>Please select workflow definition to provide workflow input</Alert>
                    ) : (
                        <>
                            {schemaLoading ? <><Spinner size={"big"}/> Loading Workflow input form... </> :
                                <>
                                    <TabInputForm
                                        stageInstanceInputSchemaMap={workflowDetails.stageInstanceInputSchemaMap || {}}
                                        formInputData={workflowInput}
                                        onTabFormChange={onTabFormDataChange}
                                        onTabFormSubmit={onTabFormDataSubmit}
                                        onTabFormError={onTabFormDataError}
                                        ref={itemsRef}
                                        enableLiveValidate={enableLiveValidate}
                                        stageInstanceUISchemaMap={workflowUISchema || {}}
                                        stageRenderingOrder={workflowDetails.stages || []}
                                        workflowInputSchema={workflowDetails.workflowInputSchema}
                                        workflowInputData={workflowInput[WORKFLOW_EXECUTION_SHARED_DATA]}
                                    />
                                </>
                            }
                        </>
                    )}
                </Container>
            </Container>
        </AWSUIForm>
    </>
}
